import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const CertificateCard = ({ certificate }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = String(date?.getDate())?.padStart(2, '0');
    const month = String(date?.getMonth() + 1)?.padStart(2, '0');
    const year = date?.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const isExpiringSoon = () => {
    const expiryDate = new Date(certificate.validUntil);
    const today = new Date();
    const daysUntilExpiry = Math.floor((expiryDate - today) / (1000 * 60 * 60 * 24));
    return daysUntilExpiry <= 90 && daysUntilExpiry > 0;
  };

  const isExpired = () => {
    const expiryDate = new Date(certificate.validUntil);
    const today = new Date();
    return expiryDate < today;
  };

  const getStatusColor = () => {
    if (isExpired()) return 'var(--color-error)';
    if (isExpiringSoon()) return 'var(--color-warning)';
    return 'var(--color-success)';
  };

  const getStatusText = () => {
    if (isExpired()) return 'Utløpt';
    if (isExpiringSoon()) return 'Utløper snart';
    return 'Aktiv';
  };

  return (
    <div className="bg-card border-2 border-border rounded-[var(--radius-md)] overflow-hidden transition-all duration-[var(--transition-base)]">
      <div className="p-6 md:p-8">
        <div className="flex flex-col sm:flex-row items-start gap-4 md:gap-6 mb-6">
          <div 
            className="flex-shrink-0 w-16 h-16 md:w-20 md:h-20 rounded-[var(--radius-sm)] flex items-center justify-center"
            style={{ background: 'rgba(0, 79, 152, 0.1)' }}
          >
            <Image
              src={certificate?.logo}
              alt={certificate?.logoAlt}
              className="w-12 h-12 md:w-16 md:h-16 object-contain"
            />
          </div>

          <div className="flex-1 min-w-0">
            <h3 className="text-lg md:text-xl font-semibold text-foreground font-heading mb-2">
              {certificate?.name}
            </h3>
            <p className="text-sm md:text-base text-muted-foreground font-caption mb-3">
              {certificate?.type}
            </p>
            <div className="flex flex-wrap items-center gap-3">
              <div 
                className="inline-flex items-center gap-2 px-3 py-1 rounded-[var(--radius-sm)] border-2"
                style={{ 
                  borderColor: getStatusColor(),
                  background: `${getStatusColor()}15`
                }}
              >
                <div 
                  className="w-2 h-2 rounded-full"
                  style={{ background: getStatusColor() }}
                />
                <span 
                  className="text-xs md:text-sm font-medium font-caption"
                  style={{ color: getStatusColor() }}
                >
                  {getStatusText()}
                </span>
              </div>
              <span className="text-xs md:text-sm text-muted-foreground font-data">
                Gyldig til: {formatDate(certificate?.validUntil)}
              </span>
            </div>
          </div>
        </div>

        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full flex items-center justify-between p-4 bg-muted rounded-[var(--radius-sm)] border-2 border-transparent hover:border-primary transition-all"
          aria-expanded={isExpanded}
          aria-label={`${isExpanded ? 'Skjul' : 'Vis'} detaljer for ${certificate?.name}`}
        >
          <span className="text-sm md:text-base font-medium text-foreground font-body">
            Sertifikatdetaljer
          </span>
          <Icon
            name={isExpanded ? 'ChevronUp' : 'ChevronDown'}
            size={20}
            color="var(--color-foreground)"
          />
        </button>

        {isExpanded && (
          <div className="mt-4 p-4 md:p-6 bg-surface-elevated rounded-[var(--radius-sm)] space-y-4">
            <div>
              <h4 className="text-sm font-semibold text-foreground font-heading mb-2">
                Utstedende myndighet
              </h4>
              <p className="text-sm text-muted-foreground font-body">
                {certificate?.issuingAuthority}
              </p>
            </div>

            <div>
              <h4 className="text-sm font-semibold text-foreground font-heading mb-2">
                Dekningsområde
              </h4>
              <p className="text-sm text-muted-foreground font-body whitespace-pre-line">
                {certificate?.coverageScope}
              </p>
            </div>

            <div>
              <h4 className="text-sm font-semibold text-foreground font-heading mb-2">
                Sertifikatnummer
              </h4>
              <p className="text-sm text-muted-foreground font-data">
                {certificate?.certificateNumber}
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
              <div>
                <h4 className="text-sm font-semibold text-foreground font-heading mb-2">
                  Utstedt dato
                </h4>
                <p className="text-sm text-muted-foreground font-data">
                  {formatDate(certificate?.issuedDate)}
                </p>
              </div>
              <div>
                <h4 className="text-sm font-semibold text-foreground font-heading mb-2">
                  Fornyelsesdato
                </h4>
                <p className="text-sm text-muted-foreground font-data">
                  {formatDate(certificate?.renewalDate)}
                </p>
              </div>
            </div>

            <div className="pt-4 border-t-2 border-border">
              <Button
                variant="outline"
                iconName="Download"
                iconPosition="left"
                onClick={() => console.log(`Downloading certificate: ${certificate?.id}`)}
                fullWidth
              >
                Last ned sertifikat
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CertificateCard;